/*
 * Auswahldialog der Fernwartung über Mesh: welche Geräte an LAN2, welche
 * Dienste je Gerät, Grund und Modus. Die Geräteliste kommt vom Server
 * (GET …/remote-targets: angelegte Geräte der Anlage und Funde der
 * Netzsuche, nach IP zusammengeführt); eine freie Adresse geht immer.
 */
import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import {
  buildOpenBody, initialSelections, manualSelection, openBlocker, usableServices,
  type Mode, type OpenBody, type Selection, type Service, type TargetSuggestion,
} from './remoteSessionModel';
import './hv-modal.css';

interface Props {
  plantId: string;
  clientId: string;
  clientLabel: string;
  services: Service[];
  canWrite: boolean;
  busyByOther: boolean;
  sessionMinutes: number;
  busy: boolean;
  error: string | null;
  onSubmit: (body: OpenBody) => void;
  onClose: () => void;
}

export function RemoteSessionDialog(props: Props) {
  const { plantId, clientId, services, canWrite } = props;
  const [selections, setSelections] = useState<Selection[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<Mode>('read');

  useEffect(() => {
    api.get<TargetSuggestion[]>(`/api/plants/${plantId}/clients/${clientId}/remote-targets`)
      .then((t) => setSelections(initialSelections(t)))
      .catch(() => {
        setListError('Die Geräteliste ließ sich nicht laden. Adressen gehen von Hand.');
        setSelections([]);
      });
  }, [plantId, clientId]);

  const usable = usableServices(services, mode);
  const list = selections ?? [];
  const blocker = openBlocker(reason, list);
  const body = buildOpenBody(reason, mode, list, services);

  function update(i: number, change: Partial<Selection>) {
    setSelections(list.map((s, j) => (j === i ? { ...s, ...change } : s)));
  }

  function toggleService(i: number, key: string, on: boolean) {
    const s = list[i];
    update(i, { services: on ? [...s.services, key] : s.services.filter((k) => k !== key) });
  }

  return (
    <div class="hv-modal-backdrop" onClick={props.onClose}>
      <div class="hv-modal hv-modal--wide hv-remote-dialog" role="dialog" aria-modal="true"
           onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Fernwartung starten · {props.clientLabel}</h2>
          <button type="button" class="hv-modal__close" onClick={props.onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <label class="hv-modal__field">
            <span>Grund (Pflicht, steht im Protokoll)</span>
            <input type="text" maxLength={200} value={reason} autoFocus
                   placeholder="z. B. Störung Heizkreis 2"
                   onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
          </label>
          <div class="hv-remote-dialog__row">
            <label class="hv-modal__field">
              <span>Modus</span>
              <select value={mode} onChange={(e) => setMode((e.target as HTMLSelectElement).value as Mode)}>
                <option value="read">Lesen</option>
                {canWrite && <option value="write">Schreiben (auch SCOPE, CODESYS, adb)</option>}
              </select>
            </label>
            <div class="hv-modal__field">
              <span>Dauer</span>
              <p class="hv-remote-dialog__fixed">{props.sessionMinutes} min, verlängerbar bis 4 h</p>
            </div>
          </div>

          <h3 class="hv-remote-dialog__heading">Geräte an LAN2</h3>
          {selections === null && <p class="muted">Lade Geräteliste…</p>}
          {listError && <p class="muted">{listError}</p>}
          {selections !== null && list.length === 0 && !listError && (
            <p class="muted">Für diese Anlage ist kein Gerät angelegt oder gefunden. Adresse von Hand eintragen.</p>
          )}
          <ul class="hv-remote-dialog__devices">
            {list.map((s, i) => (
              <li key={s.manual ? `m${i}` : s.host} class={s.checked ? 'is-checked' : ''}>
                <label class="hv-remote-dialog__device">
                  <input type="checkbox" checked={s.checked}
                         onChange={(e) => update(i, { checked: (e.target as HTMLInputElement).checked })} />
                  {s.manual ? (
                    <input type="text" class="mono" placeholder="192.168.170.100" value={s.host}
                           aria-label="Adresse im Anlagennetz"
                           onInput={(e) => update(i, { host: (e.target as HTMLInputElement).value })} />
                  ) : (
                    <span><b>{s.label}</b> <span class="muted">{s.detail}</span></span>
                  )}
                  {s.manual && (
                    <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost"
                            onClick={() => setSelections(list.filter((_, j) => j !== i))}>Entfernen</button>
                  )}
                </label>
                {s.checked && (
                  <div class="hv-remote-dialog__services">
                    {usable.map((svc) => (
                      <label key={svc.key} title={svc.description}>
                        <input type="checkbox" checked={s.services.includes(svc.key)}
                               onChange={(e) => toggleService(i, svc.key, (e.target as HTMLInputElement).checked)} />
                        {' '}{svc.key} <span class="muted">({svc.port})</span>
                      </label>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost"
                  onClick={() => setSelections([...list, manualSelection()])}>Weitere Adresse</button>
          {mode === 'read' && services.some((s) => s.requiresWrite) && (
            <p class="hv-modal__hint">
              Schreibende Dienste ({services.filter((s) => s.requiresWrite).map((s) => s.key).join(', ')})
              {canWrite ? ' gibt es im Modus Schreiben.' : ' brauchen das Recht remote.session.write.'}
            </p>
          )}
          {body.targets.length === 0 && (
            <p class="hv-modal__hint">Ohne Gerät ist nur das Panel selbst erreichbar.</p>
          )}
          {props.busyByOther && <p class="hv-remote-session__error">Am Panel läuft schon eine Sitzung eines Kollegen.</p>}
          {props.error && <p class="hv-remote-session__error">{props.error}</p>}
        </div>
        <div class="hv-modal__footer hv-remote-session__actions">
          <button type="button" class="hv-btn hv-btn--sm" onClick={props.onClose}>Abbrechen</button>
          <button type="button" class="hv-btn hv-btn--sm hv-btn--primary"
                  disabled={props.busy || blocker !== null} title={blocker ?? undefined}
                  onClick={() => props.onSubmit(body)}>
            Sitzung öffnen
          </button>
        </div>
      </div>
    </div>
  );
}
